import { Bar, BarChart, CartesianGrid, Legend, ResponsiveContainer, Tooltip, XAxis, YAxis } from 'recharts';
import type { ReportingCoverage, WindsurfToolSummary } from '@ace-tracker/shared';
import { chartAxisTickProps, chartLegendProps, chartTooltipProps, chartXAxisTickProps } from '../chartTheme';
import { WINDSURF_METRIC_INFO } from '../metricInfo';
import { ChartCard } from './ChartCard';
import { EfficiencyGauge } from './EfficiencyGauge';
import { InfoTooltip } from './InfoTooltip';
import { KpiCard } from './KpiCard';

/**
 * The Windsurf / Devin Desktop body of the dashboard (preview, activity only).
 * The collector counts Cascade conversation files by last-write time, so there
 * are no requests, models, tokens or tool calls to show, and nothing is priced.
 */
export function WindsurfDashboardBody({
  toolData,
  developersCount,
  coverage,
  SectionHeading,
}: {
  toolData: WindsurfToolSummary;
  developersCount: number;
  coverage: ReportingCoverage;
  SectionHeading: (props: { title: string; hint?: string; info?: string }) => JSX.Element;
}) {
  const grouped = groupWindsurfTimeSeries(toolData.timeSeries);

  return (
    <>
      <p className="flex items-start gap-2 rounded-card border border-border bg-warn-bg px-4 py-3 text-xs text-text-default">
        <i aria-hidden="true" className="fa-solid fa-triangle-exclamation mt-0.5 text-warn-ink" />
        <span>
          <strong className="font-medium">Preview, activity only.</strong> The Windsurf collector reads file timestamps in a layout taken from
          public research and has not yet been checked against a real install. It cannot see tokens, models or tool calls.
        </span>
      </p>

      <section>
        <SectionHeading title="Headline metrics" />
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-3">
          <KpiCard
            label="Conversations"
            value={toolData.activity.sessions.toLocaleString()}
            meta="Cascade conversations written in range"
            info={WINDSURF_METRIC_INFO.sessions}
            hero
            accentClassName="bg-primary-blue"
          />
          <KpiCard
            label="Active days"
            value={toolData.activity.activeDays.toLocaleString()}
            meta="Device-days with activity"
            info={WINDSURF_METRIC_INFO.activeDays}
            accentClassName="bg-sky-blue"
          />
          <KpiCard
            label="Active developers"
            value={String(developersCount)}
            meta={`${coverage.reportingDevices} of ${coverage.expectedDevices} devices reporting`}
            info={WINDSURF_METRIC_INFO.activeDevelopers}
            accentClassName="bg-sky-blue"
          />
        </div>
        <div className="mt-4 grid grid-cols-1 gap-4 sm:grid-cols-2">
          <EfficiencyGauge
            label="Reporting coverage"
            value={coverage.coveragePercent}
            valueLabel={`${coverage.coveragePercent}%`}
            meta={`${coverage.reportingDevices} of ${coverage.expectedDevices} devices, last 48h`}
            info={WINDSURF_METRIC_INFO.reportingCoverage}
            size="sm"
          />
        </div>
        <p className="mt-3 flex items-start gap-1.5 rounded-card border border-border bg-surface-alt px-4 py-3 text-xs text-text-muted">
          <span>
            <strong className="font-medium text-text-default">No tokens or cost shown.</strong> Windsurf records neither in a form the
            collector can read, so it is not included in Spend or any cost total.
          </span>
          <InfoTooltip text={WINDSURF_METRIC_INFO.noTokens} />
        </p>
      </section>

      <section>
        <SectionHeading title="Trends" />
        <ChartCard
          title="Engagement trend"
          caption="Cascade conversations written per day, corrected to max() per device"
          info={WINDSURF_METRIC_INFO.engagementTrend}
          height="h-96"
        >
          <ResponsiveContainer width="100%" height="100%">
            <BarChart data={grouped}>
              <CartesianGrid strokeDasharray="3 3" stroke="var(--line-soft)" />
              <XAxis dataKey="date" {...chartXAxisTickProps} />
              <YAxis {...chartAxisTickProps} />
              <Tooltip {...chartTooltipProps} />
              <Legend {...chartLegendProps} />
              <Bar dataKey="sessions" fill="#0058AB" name="Conversations" />
            </BarChart>
          </ResponsiveContainer>
        </ChartCard>
      </section>
    </>
  );
}

function groupWindsurfTimeSeries(timeSeries: { date: string; metricName: string; value: number }[]) {
  return timeSeries
    .filter((point) => point.metricName === 'windsurf.sessions')
    .map((point) => ({ date: point.date, sessions: point.value }))
    .sort((a, b) => a.date.localeCompare(b.date));
}
